import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { updateUser } from "@/integrations/supabase/user-services";
import { useToast } from "@/hooks/use-toast";

import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

// Schema for profile form validation
const profileFormSchema = z.object({
  fullName: z.string().min(2, { message: "El nombre debe tener al menos 2 caracteres." }),
  email: z.string().email({ message: "Por favor ingrese un email válido." }),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const ProfilePage = () => {
  const { profile, session } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      fullName: profile?.full_name || "",
      email: session?.user.email || "",
    },
    // Sync form with profile changes
    values: {
      fullName: profile?.full_name || "",
      email: session?.user.email || "",
    }
  });

  const { mutate: updateProfileMutation, isPending: isUpdatingProfile } = useMutation({
    mutationFn: (values: { fullName: string }) => {
      if (!profile?.id) throw new Error("User profile not found");
      return updateUser({ id: profile.id, full_name: values.fullName });
    },
    onSuccess: () => {
      toast({ title: "Nombre actualizado exitosamente." });
      queryClient.invalidateQueries({ queryKey: ['profile', profile?.id] });
    },
    onError: (error) => {
      toast({ variant: "destructive", title: "Error al actualizar el nombre.", description: error.message });
    },
  });

  const { mutate: updateEmailMutation, isPending: isUpdatingEmail } = useMutation({
    mutationFn: async (values: { email: string }) => {
      const { error } = await supabase.auth.updateUser({ email: values.email });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ 
        title: "Correo de confirmación enviado.",
        description: "Por favor, revise su bandeja de entrada (antigua y nueva) para confirmar el cambio de correo electrónico.",
      });
    },
    onError: (error) => {
      toast({ variant: "destructive", title: "Error al actualizar el email.", description: error.message });
    },
  });


  const onSubmit = (values: ProfileFormValues) => {
    // Update full name if changed
    if (values.fullName !== profile?.full_name) {
      updateProfileMutation({ fullName: values.fullName });
    }

    // Update email if changed
    if (values.email !== session?.user.email) {
      updateEmailMutation({ email: values.email });
    }
  };

  const isPending = isUpdatingProfile || isUpdatingEmail;

  return (
    <MainLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-800">Mi Perfil</h1>
        <Card>
          <CardHeader>
            <CardTitle>Información Personal</CardTitle>
            <CardDescription>Actualice su nombre y dirección de correo electrónico.</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nombre Completo</FormLabel>
                      <FormControl>
                        <Input placeholder="Su nombre completo" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Su correo electrónico" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end">
                  <Button type="submit" disabled={isPending}>
                    {isPending ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ProfilePage;
